import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useGoogleClientId } from '../hooks/useCommon';
import { useLanguage } from '../hooks/useLanguage';

// Declare global google type
declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: {
                        client_id: string;
                        callback: (response: { credential: string }) => void;
                        auto_select?: boolean;
                        cancel_on_tap_outside?: boolean;
                    }) => void;
                    renderButton: (element: HTMLElement, options: {
                        type?: 'standard' | 'icon';
                        theme?: 'outline' | 'filled_blue' | 'filled_black';
                        size?: 'large' | 'medium' | 'small';
                        text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
                        shape?: 'rectangular' | 'pill' | 'circle' | 'square';
                        logo_alignment?: 'left' | 'center';
                        width?: number;
                    }) => void;
                };
            };
        };
    }
}

interface GoogleSignInButtonProps {
    onSuccess: (credential: string) => void;
    onError: (error: string) => void;
    buttonText?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
    disabled?: boolean;
}


export default function GoogleSignInButton({
    onSuccess,
    onError,
    buttonText = 'signin_with',
    disabled = false
}: GoogleSignInButtonProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const [isReady, setIsReady] = useState(false);
    const { data: googleConfig, isLoading } = useGoogleClientId();
    const { currentLanguage } = useLanguage();

    // Store callbacks in refs so they never trigger a script reload
    // (parent re-renders create new function instances which would re-fire the effect)
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    // Track last initialized config to prevent Google's "initialize() called multiple times" warning
    // This happens when both LoginPage and RegistrationPage mount this component, or when
    // parent re-renders pass new callback instances that would otherwise re-fire the effect
    const initializedConfigRef = useRef<string | null>(null);

    // Use same source as language dropdown so button and selector always match (fixes wrong locale on refresh)
    // Priority: saved preference in localStorage > currentLanguage (avoids flicker from browser language detection)
    const savedLang = localStorage.getItem('preferred_language');
    // Normalize locale: strip region code (e.g. 'en-US' -> 'en')
    const normalizedSaved = savedLang ? savedLang.substring(0, 2).toLowerCase() : null;
    const supportedLocales = ['fr', 'es', 'en'];
    const locale = (normalizedSaved && supportedLocales.includes(normalizedSaved))
        ? normalizedSaved
        : (currentLanguage === 'fr' || currentLanguage === 'es' ? currentLanguage : 'en');

    // Load script with hl=locale so button text matches app language; re-load when locale changes
    useEffect(() => {
        if (!googleConfig?.enabled || !googleConfig?.client_id || !containerRef.current) return;

        // Skip re-initialization if client_id + locale + buttonText haven't changed
        // Prevents the "initialize() called multiple times" GSI warning when the component
        // re-mounts (e.g. both Login and Register pages) or parent callbacks change
        const configKey = `${googleConfig.client_id}-${locale}-${buttonText}`;
        if (initializedConfigRef.current === configKey && window.google?.accounts?.id) {
            return;
        }

        setIsReady(false);
        if (buttonRef.current) buttonRef.current.innerHTML = '';

        const renderButton = () => {
            if (!buttonRef.current || !window.google?.accounts?.id) return;

            // Get container width, cap at 400 (Google's max), min 200
            const containerWidth = containerRef.current?.offsetWidth || 300;
            const buttonWidth = Math.min(Math.max(containerWidth, 200), 400);

            window.google.accounts.id.initialize({
                client_id: googleConfig.client_id,
                callback: (response) => {
                    if (response.credential) {
                        onSuccessRef.current(response.credential);
                    } else {
                        onErrorRef.current('No credential received');
                    }
                },
                auto_select: false,
                cancel_on_tap_outside: true
            });

            initializedConfigRef.current = configKey;

            window.google.accounts.id.renderButton(buttonRef.current, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: buttonText,
                shape: 'rectangular',
                logo_alignment: 'left',
                width: buttonWidth
            });

            setIsReady(true);
        };

        const scriptUrl = `https://accounts.google.com/gsi/client?hl=${locale}`;
        // Always remove any existing GSI script AND clear window.google cache
        // so we reload with the correct hl= locale (avoids stale French/wrong locale)
        const existing = document.querySelector(`script[src^="https://accounts.google.com/gsi/client"]`) as HTMLScriptElement;
        if (existing) {
            existing.remove();
        }
        // Clear cached google object so the new script initializes fresh with correct locale
        if (window.google) {
            delete (window as any).google;
        }

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', renderButton);
        document.head.appendChild(script);

        return () => {
            script.removeEventListener('load', renderButton);
            if (script.parentNode) script.remove();
        };
    }, [googleConfig, buttonText, locale]);

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    // Not configured
    if (!googleConfig?.enabled) {
        return null;
    }

    return (
        <Box
            key={locale}
            ref={containerRef}
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : 'auto'
            }}
        >
            <div ref={buttonRef} />
            {!isReady && (
                <CircularProgress size={24} />
            )}
        </Box>
    );
}
